/**
 * Family Member Notes Screen
 *
 * Full-screen editor for a member's AI-context notes with an always-visible
 * Save button. Saving persists and returns to the profile that opened it.
 * Used for both the user's own ("Me") member and additional members.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getHeadingFont, getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useFamilyMember } from '@/hooks/use-family-member';

export default function FamilyMemberNotesScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const { member, isLoading, updateMember } = useFamilyMember(id ?? null);

  const initialText = useMemo(() => (member?.notes ? member.notes.join('\n') : ''), [member?.notes]);
  const [notesText, setNotesText] = useState(initialText);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setNotesText(initialText);
  }, [initialText]);

  const hasChanges = notesText !== initialText;

  const handleBack = useCallback(() => {
    router.back();
  }, [router]);

  const handleSave = useCallback(async () => {
    if (!hasChanges || isSaving) return;
    const parsedNotes = notesText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    try {
      setIsSaving(true);
      await updateMember({ notes: parsedNotes.length > 0 ? parsedNotes : null });
      router.back();
    } catch (err) {
      console.error('[family-member-notes] Failed to save notes:', err);
      Alert.alert('Error', 'Failed to save notes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [hasChanges, isSaving, notesText, updateMember, router]);

  if (isLoading || !member) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: palette.background }]} edges={['top']}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={palette.primary} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: palette.background }]} edges={['top']}>
      <View style={[styles.header, { borderBottomColor: palette.borderMuted }]}>
        <TouchableOpacity onPress={handleBack} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={palette.primary} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: palette.text, fontFamily: getHeadingFont() }]}>
          {member.isMe ? 'Your Notes' : `${member.name}'s Notes`}
        </Text>
        <TouchableOpacity
          onPress={handleSave}
          disabled={!hasChanges || isSaving}
          style={[
            styles.saveButton,
            { backgroundColor: palette.primary },
            (!hasChanges || isSaving) && styles.saveButtonDisabled,
          ]}
        >
          <Text style={[styles.saveButtonText, { fontFamily: getBodyFont() }]}>
            {isSaving ? 'Saving…' : 'Save'}
          </Text>
        </TouchableOpacity>
      </View>

      <KeyboardAvoidingView
        style={styles.keyboardView}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <Text style={[styles.helperText, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
          For AI context — one per line. Used to personalize daily summaries and chat replies.
        </Text>
        <TextInput
          style={[
            styles.textInput,
            {
              backgroundColor: palette.cardBackground,
              color: palette.text,
              borderColor: palette.border,
              fontFamily: getBodyFont(),
            },
          ]}
          value={notesText}
          onChangeText={setNotesText}
          placeholder={'e.g.\nLoves soccer\nAllergic to peanuts'}
          placeholderTextColor={palette.textMuted}
          multiline
          autoFocus
          textAlignVertical="top"
          editable={!isSaving}
        />
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  backButton: {
    padding: Spacing.xs,
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: 16,
    fontWeight: '600',
  },
  saveButton: {
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm,
    borderRadius: BorderRadius.md,
  },
  saveButtonDisabled: {
    opacity: 0.4,
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  keyboardView: {
    flex: 1,
    padding: Spacing.lg,
  },
  helperText: {
    fontSize: 13,
    marginBottom: Spacing.md,
  },
  textInput: {
    flex: 1,
    borderWidth: 1,
    borderRadius: BorderRadius.md,
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.md,
    fontSize: 16,
  },
});
